import { useCallback, useEffect, useState } from "react";
import Table from "../../common/Table";
import { useDispatch, useSelector } from "react-redux";
import { LuPencilLine, LuExternalLink, LuCopy, LuCheck } from "react-icons/lu";
import { AiOutlineDelete } from "react-icons/ai";
import { toast } from "react-toastify";
import ConfirmationPage from "../../common/ConfirmationPage";
import { deleteBlog, getBlogs } from "../../redux/services/blogSlice";
import { useNavigate } from "react-router-dom";
import { getBlogMarketingUrl, slugify } from "../../common/urlUtils";


const Blog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getBlogData = useSelector((state) => state?.blog?.blogData);

  const [page, setPage] = useState(0);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchData, setSearchData] = useState("");
  const limitOptions = [
    { value: 10, label: 10 },
    { value: 30, label: 30 },
    { value: 50, label: 50 },
    { value: 100, label: 100 },
  ];
  const [pageLimit, setPageLimit] = useState(limitOptions[0]?.value);

  const getAllBlogs = useCallback(async () => {
    try {
      await dispatch(
        getBlogs({ page: page + 1, limit: pageLimit, searchData })
      );
    } catch (error) { }
  }, [dispatch, page, pageLimit, searchData]);

  useEffect(() => {
    getAllBlogs();
  }, [getAllBlogs]);

  const deleteBlogData = async () => {
    try {
      const response = await dispatch(deleteBlog(itemToDelete));
      toast.success(response?.payload?.message, {
        autoClose: 2000,
        pauseOnHover: false,
      });
      setConfirm(false);
      getAllBlogs();
    } catch (error) { }
  };

  const copyMarketingUrl = (slug, id, category) => {
    const url = getBlogMarketingUrl(category, slug || id);
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success("Marketing URL copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2500);
  };

  const columns = [
    {
      header: "S/N",
      key: "index",
      render: (_, __, index) => page * pageLimit + index + 1,
    },
    {
      header: "Image",
      key: "image",
      render: (value) =>
        value ? (
          <div className="w-14 h-14 rounded-xl overflow-hidden border border-border">
            <img src={value} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          "—"
        ),
    },
    { 
      header: "Title", 
      key: "title",
      render: (value, row) => (
        <div>
          <div className="flex items-center gap-2">
            <p className="font-bold text-foreground text-sm line-clamp-1">{value}</p>
            {row.isFeatured && (
              <span className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500 text-white shadow-xs">
                ⭐ Featured
              </span>
            )}
          </div>
          <span className="inline-block mt-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-[#EDF4FF] text-[#124699]">
            {row.category || "UPSC Preparation"}
          </span>
        </div>
      )
    },
    { 
      header: "Marketing Slug / URL", 
      key: "slug",
      render: (value, row) => {
        const catSlug = slugify(row.category || "upsc-preparation");
        const slug = value || row._id;
        const displayPath = `/blog/${catSlug}/${slug}`;
        const fullUrl = getBlogMarketingUrl(row.category, slug);
        const isCopied = copiedId === row._id;
        return (
          <div className="flex items-center gap-2">
            <span 
              className="text-xs font-mono text-[#124699] max-w-[240px] truncate cursor-pointer hover:underline" 
              title={fullUrl}
              onClick={() => window.open(fullUrl, "_blank")}
            >
              {displayPath}
            </span>
            <button
              type="button"
              onClick={() => copyMarketingUrl(value, row._id, row.category)}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Copy marketing link"
            >
              {isCopied ? <LuCheck className="text-emerald-500 text-sm" /> : <LuCopy className="text-sm" />}
            </button>
          </div>
        );
      }
    },
  ];

  const actions = [
    {
      icon: <LuExternalLink className="w-5 h-5 text-[#124699]" />,
      tooltip: "Open live blog on website",
      onClick: (row) => {
        const url = getBlogMarketingUrl(row.category, row.slug || row._id);
        window.open(url, "_blank");
      },
    },
    {
      icon: <LuPencilLine className="w-5 h-5" />,
      onClick: (row) => {
        navigate(`/edit-blog?blogId=${row._id}`, { state: { row } });
      },
    },
    {
      icon: <AiOutlineDelete className="w-6 h-6 text-red-500" />,
      onClick: (row) => {
        setItemToDelete(row._id);
        setConfirm(true);
      },
    },
  ];

  return (
    <>
      <Table
        title="Blog"
        buttonTitle="Add Blog"
        showSearchbar={true}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setSearchData={setSearchData}
        onAddClick={() => navigate("/add-blog")}
        columns={columns}
        data={getBlogData?.blog_data || []}
        actions={actions}
        pageCount={getBlogData?.state?.page_limit}
        onPageChange={(selectedItem) => setPage(selectedItem.selected)}
        pageLimitOptions={limitOptions}
        selectedPageLimit={pageLimit}
        onPageLimitChange={setPageLimit}
      />
      {confirm && (
        <ConfirmationPage
          topicName="Blog"
          confirm={confirm}
          onDelete={deleteBlogData}
          onCancel={() => setConfirm(false)}
        />
      )}
    </>
  );
};

export default Blog;


